import { ADMIN_ROLES, type Role } from "@academichub/shared";
import type { ActivityInput } from "../api/activities";
import type { MasterUnit } from "../api/types";

/** Logika formulir kegiatan yang dipisah agar dapat diuji tanpa DOM. */

export interface ActivityFormState {
  title: string;
  description: string;
  activityDate: string;
  startTime: string;
  endTime: string;
  location: string;
  categoryId: string;
  academicYearId: string;
  semesterId: string;
  organizer: string;
  responsiblePerson: string;
  output: string;
  outcome: string;
  externalLink: string;
  studyProgramId: string;
  unitId: string;
}

export function emptyActivityForm(today = new Date()): ActivityFormState {
  return {
    title: "",
    description: "",
    activityDate: today.toISOString().slice(0, 10),
    startTime: "",
    endTime: "",
    location: "",
    categoryId: "",
    academicYearId: "",
    semesterId: "",
    organizer: "",
    responsiblePerson: "",
    output: "",
    outcome: "",
    externalLink: "",
    studyProgramId: "",
    unitId: "",
  };
}

/** Hanya admin yang boleh memilih program studi/unit secara bebas. */
export function canPickProgram(role: Role | undefined | null): boolean {
  return role ? ADMIN_ROLES.includes(role) : false;
}

/** Antrean validasi hanya untuk peran admin. */
export function isValidationQueueVisible(role: Role | undefined | null): boolean {
  return canPickProgram(role);
}

/** Unit disaring mengikuti program studi terpilih (bila ada). */
export function filterUnitsByProgram(units: MasterUnit[], studyProgramId: string): MasterUnit[] {
  if (!studyProgramId) return units;
  return units.filter((unit) => unit.studyProgramId === studyProgramId);
}

/**
 * Ubah state formulir menjadi body API.
 * Field opsional yang kosong tidak dikirim, sehingga PATCH tidak menghapus data
 * yang tidak diubah; nilai kosong yang eksplisit tetap dikirim sebagai string kosong.
 */
export function buildActivityPayload(
  form: ActivityFormState,
  options: { canPickProgram: boolean },
): ActivityInput {
  if (!form.activityDate) throw new Error("Tanggal pelaksanaan wajib diisi.");

  const payload: ActivityInput = {
    title: form.title.trim(),
    description: form.description.trim(),
    activityDate: new Date(`${form.activityDate}T00:00:00.000Z`).toISOString(),
    location: form.location.trim(),
    categoryId: form.categoryId,
    academicYearId: form.academicYearId,
    semesterId: form.semesterId,
    organizer: form.organizer.trim(),
    responsiblePerson: form.responsiblePerson.trim(),
  };

  if (form.startTime) payload.startTime = form.startTime;
  if (form.endTime) payload.endTime = form.endTime;
  if (form.output) payload.output = form.output;
  if (form.outcome) payload.outcome = form.outcome;
  if (form.externalLink) payload.externalLink = form.externalLink;
  if (options.canPickProgram && form.studyProgramId) payload.studyProgramId = form.studyProgramId;
  if (form.unitId) payload.unitId = form.unitId;

  return payload;
}
